import { useState } from 'react';
import {
  Anchor,
  Badge,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import {
  IconArrowRight,
  IconClock,
  IconGitBranch,
  IconPercentage,
  IconPhone,
  IconPhoneCall,
} from '@tabler/icons-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { useOrg } from '@/hooks/use-org';
import { CallDetailModal } from './-components/call-detail-modal';
import {
  ActiveCallSessionsCountSubscription,
  ActiveCallSessionsSubscription,
  OrganizationFlowsSubscription,
  OrganizationPhoneNumbersSubscription,
} from './-components/call-graphql';
import { formatDateTime, formatDuration, formatPhone } from './-components/format';
import { RecentCallsTable } from './-components/recent-calls-table';
import { StatCard } from './-components/stat-cards';
import { useStartOfTodayUtc } from './-components/use-time-window';
import { useCallStats } from './-components/use-call-stats';

const ACTIVE_CALLS_PAGE_SIZE = 10;

export const Route = createFileRoute('/_authed/')({
  component: DashboardPage,
});

function SectionHeader({ icon, title, to }: { icon: React.ReactNode; title: string; to: '/flows' | '/phone-numbers' }) {
  return (
    <Group justify="space-between" mb="md">
      <Group gap="xs">
        {icon}
        <Text fw={600}>{title}</Text>
      </Group>
      <Anchor component={Link} to={to} size="sm">
        <Group gap={4} wrap="nowrap">
          View all <IconArrowRight size={14} />
        </Group>
      </Anchor>
    </Group>
  );
}

function FlowsOverview({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: OrganizationFlowsSubscription, variables: { organizationId } });
  const publishedFlows = (data?.flows ?? []).filter((f) => f.currentPublicationId !== null);

  return (
    <Paper p="lg" radius="lg" withBorder>
      <SectionHeader
        icon={<IconGitBranch size={20} stroke={1.5} style={{ color: 'var(--mantine-primary-color-filled)' }} />}
        title="Flows"
        to="/flows"
      />

      {!data ? (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      ) : publishedFlows.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="md">
          No published flows yet
        </Text>
      ) : (
        <Stack gap="xs">
          {publishedFlows.slice(0, 5).map((f) => (
            <Text key={f.id} size="sm" fw={500} py={4}>
              {f.name}
            </Text>
          ))}
          {publishedFlows.length > 5 && (
            <Text size="xs" c="dimmed" ta="center" pt="xs">
              and {publishedFlows.length - 5} more
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
}

function PhoneNumbersOverview({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: OrganizationPhoneNumbersSubscription, variables: { organizationId } });
  const phoneNumbers = data?.phoneNumbers;
  const assigned = (phoneNumbers ?? []).filter((n) => n.flowId).length;

  return (
    <Paper p="lg" radius="lg" withBorder>
      <SectionHeader
        icon={<IconPhone size={20} stroke={1.5} style={{ color: 'var(--mantine-primary-color-filled)' }} />}
        title="Phone Numbers"
        to="/phone-numbers"
      />

      {!phoneNumbers ? (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      ) : phoneNumbers.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="md">
          No phone numbers configured
        </Text>
      ) : (
        <Stack gap="xs">
          <Group gap="xs" mb="xs">
            <Badge variant="light" color="green" size="sm">
              {assigned} assigned
            </Badge>
            {phoneNumbers.length - assigned > 0 && (
              <Badge variant="light" color="yellow" size="sm">
                {phoneNumbers.length - assigned} no flow
              </Badge>
            )}
          </Group>
          {phoneNumbers.slice(0, 5).map((n) => (
            <Group key={n.id} justify="space-between" py={4}>
              <Text size="sm" fw={500}>
                {formatPhone(n.phoneNumber)}
              </Text>
              <Badge variant="light" color={n.flowId ? 'green' : 'yellow'} size="xs">
                {n.flowId ? 'Assigned' : 'No flow'}
              </Badge>
            </Group>
          ))}
          {phoneNumbers.length > 5 && (
            <Text size="xs" c="dimmed" ta="center" pt="xs">
              and {phoneNumbers.length - 5} more
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
}

type ActiveSession = { id: string; fromNumber: string; toNumber: string; direction: string; startedAt: string };

function ActiveCallsSection({
  sessions,
  total,
  page,
  onPageChange,
}: {
  sessions: ActiveSession[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
}) {
  if (total === 0) return null;

  return (
    <Paper p="lg" radius="lg" withBorder>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <IconPhoneCall size={20} stroke={1.5} style={{ color: 'var(--mantine-color-green-6)' }} />
          <Text fw={600}>Active Calls</Text>
          <Badge variant="filled" color="green" size="sm">
            {total}
          </Badge>
        </Group>
      </Group>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>From</Table.Th>
            <Table.Th>To</Table.Th>
            <Table.Th>Direction</Table.Th>
            <Table.Th ta="right">Started</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sessions.map((s) => (
            <Table.Tr key={s.id}>
              <Table.Td>
                <Text size="sm">{formatPhone(s.fromNumber)}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatPhone(s.toNumber)}</Text>
              </Table.Td>
              <Table.Td>
                <Badge variant="light" color={s.direction === 'inbound' ? 'blue' : 'grape'} size="sm">
                  {s.direction}
                </Badge>
              </Table.Td>
              <Table.Td ta="right">
                <Text size="sm">{formatDateTime(s.startedAt)}</Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {total > ACTIVE_CALLS_PAGE_SIZE && (
        <Group justify="center" mt="md">
          <Pagination
            value={page}
            onChange={onPageChange}
            total={Math.ceil(total / ACTIVE_CALLS_PAGE_SIZE)}
            size="sm"
          />
        </Group>
      )}
    </Paper>
  );
}

function DashboardPage() {
  const { organizationId, currentOrg, isLoading } = useOrg();
  const [activeCallsPage, setActiveCallsPage] = useState(1);
  const [selectedCallControlId, setSelectedCallControlId] = useState<string | null>(null);

  const isAdmin = currentOrg?.role === 'owner' || currentOrg?.role === 'admin';
  const todayStart = useStartOfTodayUtc();
  const stats = useCallStats(organizationId, todayStart);

  const [{ data: activeSessions }] = useSubscription({
    query: ActiveCallSessionsSubscription,
    variables: {
      organizationId: organizationId ?? '',
      limit: ACTIVE_CALLS_PAGE_SIZE,
      offset: (activeCallsPage - 1) * ACTIVE_CALLS_PAGE_SIZE,
    },
    pause: !organizationId,
  });

  const [{ data: activeSessionCount }] = useSubscription({
    query: ActiveCallSessionsCountSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const activeCount = activeSessionCount?.callSessionsAggregate.aggregate?.count ?? 0;

  if (isLoading || (organizationId && !stats)) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId || !stats) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  const successRate = stats.totalCalls > 0 ? Math.round((stats.completedCalls / stats.totalCalls) * 100) : 0;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Dashboard</Title>
          <Text c="dimmed" mt="xs">
            Operational overview
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
          <StatCard title="Active Calls" value={activeCount.toString()} icon={IconPhoneCall} color="indigo" />
          <StatCard title="Today's Calls" value={stats.totalCalls.toString()} icon={IconPhone} color="blue" />
          <StatCard title="Success Rate" value={`${successRate}%`} icon={IconPercentage} color="green" />
          <StatCard
            title="Avg Duration"
            value={formatDuration(stats.averageDurationSeconds)}
            icon={IconClock}
            color="grape"
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <FlowsOverview organizationId={organizationId} />
          <PhoneNumbersOverview organizationId={organizationId} />
        </SimpleGrid>

        <ActiveCallsSection
          sessions={activeSessions?.callSessions ?? []}
          total={activeCount}
          page={activeCallsPage}
          onPageChange={setActiveCallsPage}
        />

        <Paper p="lg" radius="lg" withBorder>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <IconPhone size={20} stroke={1.5} style={{ color: 'var(--mantine-primary-color-filled)' }} />
              <Text fw={600}>Recent Calls</Text>
            </Group>
            <Anchor component={Link} to={isAdmin ? '/calls' : '/analytics'} size="sm">
              <Group gap={4} wrap="nowrap">
                View all <IconArrowRight size={14} />
              </Group>
            </Anchor>
          </Group>
          <RecentCallsTable
            organizationId={organizationId}
            limit={5}
            onSelect={isAdmin ? setSelectedCallControlId : undefined}
          />
        </Paper>
      </Stack>

      {isAdmin && (
        <CallDetailModal
          organizationId={organizationId}
          callControlId={selectedCallControlId}
          onClose={() => setSelectedCallControlId(null)}
        />
      )}
    </Container>
  );
}
